import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Save, Building2, Loader2, Ruler, Radio, Plus, Pencil, Trash2, LayoutGrid, FileText, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toastApiError } from '@/lib/errors';
import EmptyState from '@/components/EmptyState';
import useLaboratories from '@/hooks/useLaboratories';

// Quantity = code laboratoře. Single source of truth na BE
// (`app/domain/laboratories.py`), do FE se načítá přes `useLaboratories()`.
// `QUANTITIES`/`QUANTITY_LABEL` se odvozují uvnitř komponent z hooku (label = name).

// `required: true` = povinné pro kompletní hlavičku/patičku KL (vynucuje i BE
// při ukládání i při schválení kalibrace — completeness gate).
const FIELDS = [
  { key: 'labName', label: 'Název laboratoře', placeholder: 'Kalibrační laboratoř ČHMÚ', required: true },
  { key: 'accreditationNumber', label: 'Číslo akreditace (ČIA)', placeholder: 'V-0XXX', required: true },
  { key: 'contactPerson', label: 'Kontaktní osoba', placeholder: 'Ing. Jan Novák' },
  { key: 'street', label: 'Ulice a číslo', placeholder: 'Na Šabatce 2050/17', required: true },
  { key: 'city', label: 'Město', placeholder: 'Praha 4', required: true },
  { key: 'postalCode', label: 'PSČ', placeholder: '143 06', required: true },
  { key: 'country', label: 'Země', placeholder: 'Česká republika' },
  { key: 'phone', label: 'Telefon', placeholder: '+420 xxx xxx xxx' },
  { key: 'email', label: 'E-mail', placeholder: 'lab@example.cz', required: true },
  { key: 'web', label: 'Web', placeholder: 'https://www.example.cz' },
];

// Podpole šablony kalibračního listu (musí odpovídat certificateTemplate na BE).
const TEMPLATE_FIELDS = [
  { key: 'headerText', label: 'Hlavička KL', placeholder: 'Akreditovaná kalibrační laboratoř č. 1234' },
  { key: 'footerText', label: 'Patička KL', placeholder: 'Tento kalibrační list nesmí být reprodukován jinak než celý.' },
  { key: 'accreditationMark', label: 'Značka akreditace', placeholder: 'ČIA L 1234' },
  { key: 'logoUrl', label: 'URL loga', placeholder: 'https://…/logo.png' },
];

const TABS = [
  { id: 'lab', label: 'Laboratoř', icon: Building2 },
  { id: 'quantities', label: 'Veličiny', icon: LayoutGrid },
  { id: 'units', label: 'Jednotky', icon: Ruler },
  { id: 'probeTypes', label: 'Typy sond', icon: Radio },
];

// Popisné labely veličin pro dlaždice (klíč = code laboratoře / balíčku).
const QUANTITY_LABELS = {
  hmotnost: 'Hmotnost', teplota: 'Teplota', tlak: 'Tlak', vlhkost: 'Vlhkost',
  srazky: 'Srážky', proudeni: 'Proudění vzduchu', smer: 'Směr větru',
};

function QuantitiesTab() {
  const [quantities, setQuantities] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/lab-settings/quantities'),
      api.get('/module-requests').catch(() => ({ data: [] })),
    ])
      .then(([q, r]) => { setQuantities(q.data); setRequests(r.data || []); })
      .catch(() => toast.error('Nepodařilo se načíst veličiny'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Veličiny, pro které už běží čekající žádost o modul.
  const pendingKeys = useMemo(() => {
    const s = new Set();
    for (const r of requests) {
      if (r.status === 'pending') (r.requestedQuantities || []).forEach(k => s.add(k));
    }
    return s;
  }, [requests]);

  const requestModule = async (q) => {
    setSavingKey(q.key);
    try {
      await api.post('/module-requests', { quantities: [q.key] });
      toast.success(`Žádost o modul „${QUANTITY_LABELS[q.key] || q.key}" odeslána`);
      load();
    } catch (e) {
      toastApiError(e, 'Žádost se nepodařilo odeslat');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Načítám…</div>;

  return (
    <div className="space-y-3 max-w-3xl">
      <p className="ct-page-subtitle">
        Zapnuté veličiny (moduly) se objeví v kalibračním workflow. O zapnutí dalších
        modulů můžete požádat — žádost schválí a modul dodá tým CalibraTech.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="quantities-tiles">
        {quantities.map(q => {
          const isPending = pendingKeys.has(q.key);
          return (
            <div
              key={q.key}
              data-testid={`quantity-tile-${q.key}`}
              className="ct-panel p-4 text-left relative flex flex-col"
              style={{
                borderColor: q.enabled ? 'var(--ct-accent)' : 'var(--ct-border)',
                borderWidth: q.enabled ? 2 : 1,
                opacity: q.enabled ? 1 : 0.85,
              }}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{QUANTITY_LABELS[q.key] || q.key}</span>
                {q.enabled && <Check className="w-4 h-4" style={{ color: 'var(--ct-accent)' }} />}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-xs px-1.5 py-0.5 rounded" style={{
                  background: q.enabled ? 'var(--ct-accent)' : 'var(--ct-border)',
                  color: q.enabled ? '#fff' : 'var(--ct-text-muted)',
                }}>{q.enabled ? 'Zapnuto' : 'Nezapnuto'}</span>
                {q.validation === 'demo' && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">demo</span>
                )}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">v{q.version}</div>
              {!q.enabled && (
                <div className="mt-3">
                  {isPending ? (
                    <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">
                      Žádost odeslána — čeká na vyřízení
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full gap-1.5"
                      disabled={savingKey === q.key}
                      onClick={() => requestModule(q)}
                    >
                      {savingKey === q.key
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Plus className="w-3.5 h-3.5" />}
                      Požádat o modul
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors"
      style={{
        color: active ? 'var(--ct-accent)' : 'var(--ct-text-secondary)',
        borderBottom: active ? '2px solid var(--ct-accent)' : '2px solid transparent',
      }}
    >
      <Icon className="w-4 h-4" />
      {tab.label}
    </button>
  );
}

/**
 * Generická master-data tabulka — pro jednotky a typy sond.
 * `kind` = 'unit' | 'probeType'.
 * `fields` = pole polí pro form ({ key, label, required }).
 */
function MasterDataDialog({ open, onClose, kind, item, onSaved }) {
  const labs = useLaboratories();
  const QUANTITIES = labs.map((l) => ({ code: l.code, label: l.name }));
  const isEdit = !!item?.id;
  const [form, setForm] = useState({
    code: '', symbol: '', name: '', description: '',
    quantity: '', baseUnit: false, conversionFactor: 1, conversionOffset: 0,
    laboratory: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (item) {
      setForm({
        code: item.code || '',
        symbol: item.symbol || '',
        name: item.name || '',
        description: item.description || '',
        quantity: item.quantity || '',
        baseUnit: !!item.baseUnit,
        conversionFactor: item.conversionFactor ?? 1,
        conversionOffset: item.conversionOffset ?? 0,
        laboratory: item.laboratory || '',
      });
    } else {
      setForm({
        code: '', symbol: '', name: '', description: '',
        quantity: '', baseUnit: false, conversionFactor: 1, conversionOffset: 0,
        laboratory: '',
      });
    }
  }, [open, item]);

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error('Vyplňte kód'); return; }
    if (kind === 'unit') {
      if (!form.symbol.trim()) { toast.error('Vyplňte symbol'); return; }
      if (!form.quantity) { toast.error('Vyberte veličinu'); return; }
      const factor = parseFloat(form.conversionFactor);
      if (isNaN(factor) || factor === 0) {
        toast.error('Faktor musí být číslo různé od nuly');
        return;
      }
      const offset = parseFloat(form.conversionOffset);
      if (isNaN(offset)) {
        toast.error('Offset musí být číslo');
        return;
      }
    }
    if (kind === 'probeType') {
      if (!form.name.trim()) { toast.error('Vyplňte název'); return; }
      if (!form.laboratory) { toast.error('Vyberte laboratoř'); return; }
    }
    setSaving(true);
    try {
      const endpoint = kind === 'unit' ? '/units' : '/probe-types';
      const payload = kind === 'unit'
        ? {
            code: form.code.trim(),
            symbol: form.symbol.trim(),
            description: form.description.trim(),
            quantity: form.quantity,
            baseUnit: !!form.baseUnit,
            conversionFactor: parseFloat(form.conversionFactor),
            conversionOffset: parseFloat(form.conversionOffset),
          }
        : {
            code: form.code.trim(),
            name: form.name.trim(),
            description: form.description.trim(),
            laboratory: form.laboratory,
          };
      if (isEdit) {
        await api.put(`${endpoint}/${item.id}`, payload);
        toast.success('Upraveno');
      } else {
        await api.post(endpoint, payload);
        toast.success('Vytvořeno');
      }
      onSaved();
      onClose();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se uložit');
    } finally {
      setSaving(false);
    }
  };

  const titleText = kind === 'unit'
    ? (isEdit ? 'Upravit jednotku' : 'Nová jednotka')
    : (isEdit ? 'Upravit typ sondy' : 'Nový typ sondy');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', maxWidth: '480px' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--ct-accent)' }}>{titleText}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="ct-form-label">Kód (slug, unique)</label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder={kind === 'unit' ? 'degC' : 'pt100'}
              disabled={isEdit}
              title={isEdit ? 'Kód nelze měnit po vytvoření' : undefined}
            />
          </div>
          {kind === 'unit' ? (
            <>
              <div>
                <label className="ct-form-label">Symbol</label>
                <Input
                  value={form.symbol}
                  onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                  placeholder="°C, bar, %"
                />
              </div>
              <div>
                <label className="ct-form-label">Veličina (laboratoř)</label>
                <select
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="w-full h-9 px-2 rounded border text-sm"
                  style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                >
                  <option value="">— vyberte —</option>
                  {QUANTITIES.map(q => (
                    <option key={q.code} value={q.code}>{q.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="baseUnit"
                  checked={!!form.baseUnit}
                  onCheckedChange={(v) => setForm({ ...form, baseUnit: !!v })}
                />
                <label htmlFor="baseUnit" className="text-sm cursor-pointer">
                  Základní jednotka veličiny
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="ct-form-label">Faktor (× základní)</label>
                  <Input
                    type="number"
                    step="any"
                    value={form.conversionFactor}
                    onChange={(e) => setForm({ ...form, conversionFactor: e.target.value })}
                    placeholder="1"
                    disabled={form.baseUnit}
                    title={form.baseUnit ? 'Pro základní jednotku je faktor vždy 1' : undefined}
                  />
                </div>
                <div>
                  <label className="ct-form-label">Offset</label>
                  <Input
                    type="number"
                    step="any"
                    value={form.conversionOffset}
                    onChange={(e) => setForm({ ...form, conversionOffset: e.target.value })}
                    placeholder="0"
                    disabled={form.baseUnit}
                    title={form.baseUnit ? 'Pro základní jednotku je offset vždy 0' : undefined}
                  />
                </div>
              </div>
              <p className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
                Převod na základní jednotku: <code>base = value × faktor + offset</code>.
                Příklad: 1 bar = 100 000 Pa (faktor 100 000), 0 °C = 273,15 K (faktor 1, offset 273,15).
              </p>
            </>
          ) : (
            <>
              <div>
                <label className="ct-form-label">Název</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Pt100, Termočlánek K"
                />
              </div>
              <div>
                <label className="ct-form-label">Laboratoř (veličina)</label>
                <select
                  value={form.laboratory}
                  onChange={(e) => setForm({ ...form, laboratory: e.target.value })}
                  className="w-full h-9 px-2 rounded border text-sm"
                  style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
                >
                  <option value="">— vyberte —</option>
                  {QUANTITIES.map(q => (
                    <option key={q.code} value={q.code}>{q.label}</option>
                  ))}
                </select>
                <p className="text-xs mt-1" style={{ color: 'var(--ct-text-disabled)' }}>
                  Filtruje typ sondy v dropdownu při zakládání sond — zobrazí se jen typy
                  odpovídající laboratoři vybrané metodiky.
                </p>
              </div>
            </>
          )}
          <div>
            <label className="ct-form-label">Popis</label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="ct-btn-secondary" disabled={saving}>Zrušit</Button>
          <Button onClick={handleSave} className="ct-btn-primary" disabled={saving}>
            {saving ? 'Ukládám…' : (isEdit ? 'Uložit' : 'Vytvořit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Jednotky group by quantity (laboratoř). Per veličina sekce
 * s tabulkou; baseUnit má visual badge. Veličiny bez jednotky sekci skryjí.
 */
function UnitsGroupedTable({ items, onEdit, onDelete }) {
  const labs = useLaboratories();
  const QUANTITIES = labs.map((l) => ({ code: l.code, label: l.name }));
  const QUANTITY_LABEL = Object.fromEntries(labs.map((l) => [l.code, l.name]));
  const grouped = useMemo(() => {
    const acc = {};
    items.forEach(u => {
      const q = u.quantity || '_unassigned';
      if (!acc[q]) acc[q] = [];
      acc[q].push(u);
    });
    // Within each group: baseUnit first, then alphabetically by symbol
    Object.values(acc).forEach(list => {
      list.sort((a, b) => {
        if (a.baseUnit && !b.baseUnit) return -1;
        if (!a.baseUnit && b.baseUnit) return 1;
        return (a.symbol || '').localeCompare(b.symbol || '');
      });
    });
    return acc;
  }, [items]);

  // Pořadí veličin = pořadí v QUANTITIES (deterministické), unassigned na konci
  const orderedQuantities = [...QUANTITIES.map(q => q.code), '_unassigned'].filter(q => grouped[q]);

  return (
    <div className="space-y-4">
      {orderedQuantities.map(q => {
        const list = grouped[q];
        const label = q === '_unassigned'
          ? 'Bez veličiny (chybí migrace)'
          : QUANTITY_LABEL[q] || q;
        return (
          <div key={q} className="ct-panel overflow-hidden">
            <div className="ct-panel-header">{label}</div>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--ct-bg-secondary)' }}>
                  <th className="text-left py-2 px-3 ct-eyebrow">Kód</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">Symbol</th>
                  <th className="text-right py-2 px-3 ct-eyebrow">Faktor</th>
                  <th className="text-right py-2 px-3 ct-eyebrow">Offset</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">Popis</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {list.map(u => (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--ct-border)' }}>
                    <td className="py-2 px-3 font-mono text-xs">{u.code}</td>
                    <td className="py-2 px-3">
                      <span>{u.symbol}</span>
                      {u.baseUnit && (
                        <span
                          className="ml-2 text-[10px] font-bold rounded px-1.5 py-0.5"
                          style={{ background: 'var(--ct-accent)', color: '#fff' }}
                          title="Základní jednotka veličiny"
                        >
                          ZÁKLADNÍ
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {u.baseUnit ? '—' : (u.conversionFactor ?? 1)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {u.baseUnit ? '—' : (u.conversionOffset ?? 0)}
                    </td>
                    <td className="py-2 px-3 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                      {u.description || '—'}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => onEdit(u)}
                          title="Upravit"
                          className="p-1 rounded transition-opacity hover:opacity-70"
                          style={{ color: 'var(--ct-text-secondary)' }}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(u)}
                          title="Smazat"
                          className="p-1 rounded transition-opacity hover:opacity-70"
                          style={{ color: 'var(--ct-error)' }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Typy sond group by laboratory. Per laboratoř sekce s tabulkou,
 * pre-migration typy bez `laboratory` jdou do sekce „Bez laboratoře".
 */
function ProbeTypesGroupedTable({ items, onEdit, onDelete }) {
  const labs = useLaboratories();
  const QUANTITIES = labs.map((l) => ({ code: l.code, label: l.name }));
  const QUANTITY_LABEL = Object.fromEntries(labs.map((l) => [l.code, l.name]));
  const grouped = useMemo(() => {
    const acc = {};
    items.forEach(pt => {
      const lab = pt.laboratory || '_unassigned';
      if (!acc[lab]) acc[lab] = [];
      acc[lab].push(pt);
    });
    Object.values(acc).forEach(list => {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'cs'));
    });
    return acc;
  }, [items]);

  const orderedLabs = [...QUANTITIES.map(q => q.code), '_unassigned'].filter(l => grouped[l]);

  return (
    <div className="space-y-4">
      {orderedLabs.map(lab => {
        const list = grouped[lab];
        const label = lab === '_unassigned'
          ? 'Bez laboratoře (chybí migrace)'
          : QUANTITY_LABEL[lab] || lab;
        return (
          <div key={lab} className="ct-panel overflow-hidden">
            <div className="ct-panel-header">{label}</div>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--ct-bg-secondary)' }}>
                  <th className="text-left py-2 px-3 ct-eyebrow">Kód</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">Název</th>
                  <th className="text-left py-2 px-3 ct-eyebrow">Popis</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {list.map(pt => (
                  <tr key={pt.id} style={{ borderTop: '1px solid var(--ct-border)' }}>
                    <td className="py-2 px-3 font-mono text-xs">{pt.code}</td>
                    <td className="py-2 px-3">{pt.name}</td>
                    <td className="py-2 px-3 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                      {pt.description || '—'}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => onEdit(pt)}
                          title="Upravit"
                          className="p-1 rounded transition-opacity hover:opacity-70"
                          style={{ color: 'var(--ct-text-secondary)' }}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(pt)}
                          title="Smazat"
                          className="p-1 rounded transition-opacity hover:opacity-70"
                          style={{ color: 'var(--ct-error)' }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function MasterDataTab({ kind }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const endpoint = kind === 'unit' ? '/units' : '/probe-types';

  const load = useCallback(() => {
    setLoading(true);
    api.get(endpoint)
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (item) => {
    const label = kind === 'unit' ? `jednotku ${item.symbol}` : `typ sondy ${item.name}`;
    if (!window.confirm(`Smazat ${label}? Sondy odkazující na tento záznam zablokují smazání.`)) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success('Smazáno');
      load();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se smazat');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
          {kind === 'unit'
            ? 'Měřicí jednotky používané sondami (°C, bar, %, m/s, mm…). Mazání blokováno, pokud je jednotka v použití.'
            : 'Typy sond (Pt100, kapacitní, deformační…). Mazání blokováno, pokud je typ v použití.'}
        </p>
        <Button onClick={() => { setEditingItem(null); setDialogOpen(true); }} className="ct-btn-primary gap-1 text-xs">
          <Plus className="w-3.5 h-3.5" /> {kind === 'unit' ? 'Nová jednotka' : 'Nový typ sondy'}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-sm" style={{ color: 'var(--ct-text-disabled)' }}>Načítám…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={kind === 'unit' ? Ruler : Radio}
          title={kind === 'unit' ? 'Žádné jednotky' : 'Žádné typy sond'}
          description="Přidejte první záznam tlačítkem nahoře."
        />
      ) : kind === 'unit' ? (
        <UnitsGroupedTable
          items={items}
          onEdit={(it) => { setEditingItem(it); setDialogOpen(true); }}
          onDelete={handleDelete}
        />
      ) : kind === 'probeType' ? (
        <ProbeTypesGroupedTable
          items={items}
          onEdit={(it) => { setEditingItem(it); setDialogOpen(true); }}
          onDelete={handleDelete}
        />
      ) : (
        <div className="ct-panel overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--ct-bg-secondary)' }}>
                <th className="text-left py-2 px-3 ct-eyebrow">Kód</th>
                <th className="text-left py-2 px-3 ct-eyebrow">{kind === 'unit' ? 'Symbol' : 'Název'}</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Popis</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} style={{ borderTop: '1px solid var(--ct-border)' }}>
                  <td className="py-2 px-3 font-mono text-xs">{it.code}</td>
                  <td className="py-2 px-3">{kind === 'unit' ? it.symbol : it.name}</td>
                  <td className="py-2 px-3 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{it.description || '—'}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        type="button"
                        onClick={() => { setEditingItem(it); setDialogOpen(true); }}
                        title="Upravit"
                        className="p-1 rounded transition-opacity hover:opacity-70"
                        style={{ color: 'var(--ct-text-secondary)' }}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(it)}
                        title="Smazat"
                        className="p-1 rounded transition-opacity hover:opacity-70"
                        style={{ color: 'var(--ct-error)' }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MasterDataDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        kind={kind}
        item={editingItem}
        onSaved={load}
      />
    </div>
  );
}

function LabFormTab({ form, setForm, saving, onSave }) {
  return (
    <div className="space-y-4 max-w-2xl">
      <div className="ct-panel">
        <div className="ct-panel-header">Identifikace laboratoře</div>
        <div className="p-4 space-y-3">
          {FIELDS.map(f => (
            <div key={f.key} className="ct-form-field">
              <label className="ct-form-label">
                {f.label}{f.required && <span style={{ color: 'var(--ct-error)' }}> *</span>}
              </label>
              <Input
                data-testid={`lab-${f.key}`}
                value={form[f.key] || ''}
                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="h-9 text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="ct-panel">
        <div className="ct-panel-header flex items-center gap-2">
          <FileText className="w-4 h-4" /> Šablona kalibračního listu
        </div>
        <div className="p-4 space-y-3">
          {TEMPLATE_FIELDS.map(f => (
            <div key={f.key} className="ct-form-field">
              <label className="ct-form-label">{f.label}</label>
              <Input
                data-testid={`tmpl-${f.key}`}
                value={(form.certificateTemplate || {})[f.key] || ''}
                onChange={e => setForm(prev => ({
                  ...prev,
                  certificateTemplate: { ...(prev.certificateTemplate || {}), [f.key]: e.target.value },
                }))}
                placeholder={f.placeholder}
                className="h-9 text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="ct-panel">
        <div className="ct-panel-header flex items-center gap-2">
          <Check className="w-4 h-4" /> Prohlášení o shodě
        </div>
        <div className="p-4 space-y-2">
          <div className="ct-form-field">
            <label className="ct-form-label">Rozhodovací pravidlo</label>
            <select
              data-testid="lab-decisionRule"
              value={form.decisionRule || 'simple'}
              onChange={e => setForm(prev => ({ ...prev, decisionRule: e.target.value }))}
              className="h-9 text-sm rounded px-2"
              style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' }}
            >
              <option value="simple">Prosté přijetí (ILAC-G8-4.2.3)</option>
              <option value="guard_band">Guarded acceptance — ochranné pásmo = U (ILAC-G8-4.2.4)</option>
            </select>
          </div>
          <p className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
            {form.decisionRule === 'guard_band'
              ? 'Shoda při |chyba| ≤ MPE − U (přísnější, snižuje riziko chybného přijetí). Tiskne se na kalibrační list i do DCC.'
              : 'Shoda při |chyba| ≤ MPE; nejistota se neodečítá. Tiskne se na kalibrační list i do DCC.'}
          </p>
        </div>
      </div>

      <div className="ct-panel">
        <div className="ct-panel-header flex items-center gap-2">
          <Building2 className="w-4 h-4" /> Adresář zákazníků
        </div>
        <div className="p-4 space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox
              data-testid="lab-listedInDirectory"
              checked={!!form.listedInDirectory}
              onCheckedChange={v => setForm(prev => ({ ...prev, listedInDirectory: !!v }))}
              className="mt-0.5"
            />
            <span className="text-sm">
              Zveřejnit laboratoř v adresáři zákaznického portálu
              <span className="block text-xs mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>
                Vaši zákazníci si při registraci na portálu budou moci vybrat vaši
                laboratoř. Bez zaškrtnutí zůstane laboratoř skrytá.
              </span>
            </span>
          </label>
        </div>
      </div>

      <Button
        data-testid="save-lab-settings-btn"
        onClick={onSave}
        disabled={saving}
        className="ct-btn-primary gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Uložit nastavení
      </Button>
    </div>
  );
}

export default function LabSettingsPage() {
  const [activeTab, setActiveTab] = useState('lab');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/lab-settings').then(r => {
      setForm(r.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    const missing = FIELDS.filter(f => f.required && !String(form[f.key] || '').trim()).map(f => f.label);
    if (missing.length > 0) {
      toast.error(`Vyplňte povinná pole: ${missing.join(', ')}.`);
      setActiveTab('lab');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/lab-settings', form);
      setForm(res.data);
      toast.success('Nastavení laboratoře uloženo');
    } catch {
      toast.error('Chyba při ukládání');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div data-testid="lab-settings-page" className="space-y-4">
      <div>
        <h1 className="ct-h1 flex items-center gap-2">
          <Building2 className="w-5 h-5" style={{ color: 'var(--ct-accent)' }} />
          Nastavení laboratoře
        </h1>
        <p className="ct-page-subtitle">
          Údaje pro DCC certifikáty + master data pro sondy (jednotky, typy sond)
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b overflow-x-auto" style={{ borderColor: 'var(--ct-border)' }}>
        {TABS.map(t => (
          <TabButton
            key={t.id}
            tab={t}
            active={activeTab === t.id}
            onClick={() => setActiveTab(t.id)}
          />
        ))}
      </div>

      <div>
        {activeTab === 'lab' && <LabFormTab form={form} setForm={setForm} saving={saving} onSave={handleSave} />}
        {activeTab === 'quantities' && <QuantitiesTab />}
        {activeTab === 'units' && <MasterDataTab kind="unit" />}
        {activeTab === 'probeTypes' && <MasterDataTab kind="probeType" />}
      </div>
    </div>
  );
}
